import React, { useState, useEffect } from 'react';

const AdminEmailSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({
        smtp: {
            host: '',
            port: 587,
            secure: false,
            user: '',
            password: ''
        },
        imap: {
            host: '',
            port: 993,
            secure: true,
            user: '',
            password: ''
        },
        fromEmail: '',
        fromName: ''
    });

    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    const getAuthHeaders = React.useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }, []);

    const loadConfig = React.useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/communication/email-config`, {
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                if (data) {
                    setConfig(data);
                }
            }
        } catch (error) {
            console.error('Error loading config:', error);
        } finally {
            setLoading(false);
        }
    }, [API_URL, getAuthHeaders]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const response = await fetch(`${API_URL}/api/communication/email-config`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(config)
            });

            if (response.ok) {
                alert('Email settings saved successfully!');
            } else {
                const error = await response.json();
                alert('Error: ' + (error.detail || 'Failed to save settings'));
            }
        } catch (error) {
            console.error('Error saving config:', error);
            alert('Error saving settings');
        } finally {
            setSaving(false);
        }
    };

    const updateSMTP = (field, value) => {
        setConfig(prev => ({
            ...prev,
            smtp: { ...prev.smtp, [field]: value }
        }));
    };

    const updateIMAP = (field, value) => {
        setConfig(prev => ({
            ...prev,
            imap: { ...prev.imap, [field]: value }
        }));
    };

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-3xl font-bold">Email Settings</h2>
                <p className="text-gray-600 mt-2">Configure SMTP and IMAP settings for email communication</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* General Settings */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-xl font-bold mb-4">General Settings</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">From Email *</label>
                            <input
                                type="email"
                                value={config.fromEmail}
                                onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="noreply@example.com"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">From Name *</label>
                            <input
                                type="text"
                                value={config.fromName}
                                onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="VishwaVoyagerz"
                            />
                        </div>
                    </div>
                </div>

                {/* SMTP Settings */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-xl font-bold mb-4">
                        <i className="fas fa-paper-plane mr-2 text-blue-600"></i>
                        SMTP Settings (Outgoing Mail)
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">SMTP Host *</label>
                            <input
                                type="text"
                                value={config.smtp.host}
                                onChange={(e) => updateSMTP('host', e.target.value)}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="smtp.gmail.com"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">SMTP Port *</label>
                            <input
                                type="number"
                                value={config.smtp.port}
                                onChange={(e) => updateSMTP('port', parseInt(e.target.value))}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="587"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">SMTP Username *</label>
                            <input
                                type="text"
                                value={config.smtp.user}
                                onChange={(e) => updateSMTP('user', e.target.value)}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="your-email@gmail.com"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">SMTP Password *</label>
                            <input
                                type="password"
                                value={config.smtp.password}
                                onChange={(e) => updateSMTP('password', e.target.value)}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="••••••••"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={config.smtp.secure}
                                    onChange={(e) => updateSMTP('secure', e.target.checked)}
                                    className="mr-2"
                                />
                                <span>Use SSL/TLS</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* IMAP Settings */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-xl font-bold mb-4">
                        <i className="fas fa-inbox mr-2 text-green-600"></i>
                        IMAP Settings (Incoming Mail)
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">IMAP Host *</label>
                            <input
                                type="text"
                                value={config.imap.host}
                                onChange={(e) => updateIMAP('host', e.target.value)}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="imap.gmail.com"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">IMAP Port *</label>
                            <input
                                type="number"
                                value={config.imap.port}
                                onChange={(e) => updateIMAP('port', parseInt(e.target.value))}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="993"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">IMAP Username *</label>
                            <input
                                type="text"
                                value={config.imap.user}
                                onChange={(e) => updateIMAP('user', e.target.value)}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="your-email@gmail.com"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">IMAP Password *</label>
                            <input
                                type="password"
                                value={config.imap.password}
                                onChange={(e) => updateIMAP('password', e.target.value)}
                                required
                                className="w-full px-4 py-2 border rounded-lg"
                                placeholder="••••••••"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={config.imap.secure}
                                    onChange={(e) => updateIMAP('secure', e.target.checked)}
                                    className="mr-2"
                                />
                                <span>Use SSL/TLS</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">
                        <i className="fas fa-info-circle mr-2"></i>Configuration Tips
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                        <li>• For Gmail: Use App Passwords instead of your regular password</li>
                        <li>• SMTP Port 587 (TLS) or 465 (SSL) for most providers</li>
                        <li>• IMAP Port 993 (SSL) for most providers</li>
                        <li>• Ensure "Less secure app access" is enabled if required</li>
                    </ul>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        <i className="fas fa-save mr-2"></i>
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminEmailSettings;
